import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import artworkService from './artworkService';
import { toast } from 'react-toastify';

const sortCategoriesByPreference = (items = []) =>
    items.sort((a, b) => {
        const orderA = typeof a.sort_order === 'number' ? a.sort_order : 999;
        const orderB = typeof b.sort_order === 'number' ? b.sort_order : 999;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        const labelA = a.display_name || a.category || '';
        const labelB = b.display_name || b.category || '';
        return labelA.localeCompare(labelB);
    });

const initialState = {
    artworks: [],
    artwork: null,
    categories: [],
    categoriesLoading: false,
    categoriesError: false,
    categoriesMessage: '',
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: '',
};

// Create artwork
export const createArtwork = createAsyncThunk(
    'artwork/create',
    async (artworkData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;

            // Upload image
            const imageName = await artworkService.uploadImage(artworkData.image);

            const finalArtworkData = {
                ...artworkData,
                image: imageName,
            };

            return await artworkService.addArtwork(finalArtworkData, token);

        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Update artwork
export const updateArtwork = createAsyncThunk(
    'artwork/update',
    async ({ id, artworkData }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;

            // Upload image if it's a new File
            const imageName = await artworkService.uploadImage(artworkData.image);

            const finalArtworkData = {
                ...artworkData,
                image: imageName,
            };

            return await artworkService.updateArtwork(id, finalArtworkData, token);

        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const updateArtworkFields = createAsyncThunk(
    'artwork/updateFields',
    async ({ id, data }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await artworkService.updateArtworkFields(id, data, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get all admin artworks
export const getAdminArtworks = createAsyncThunk(
    'artwork/getAllAdmin',
    async (_, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await artworkService.getAdminArtworks(token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get artwork by ID
export const getArtworkById = createAsyncThunk(
    'artwork/getById',
    async (id, thunkAPI) => {
        try {
            return await artworkService.getArtworkById(id);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Delete artwork
export const deleteArtwork = createAsyncThunk(
    'artwork/delete',
    async (id, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await artworkService.deleteArtwork(id, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const restoreArtwork = createAsyncThunk(
    'artwork/restore',
    async (id, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await artworkService.restoreArtwork(id, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const getArtworks = createAsyncThunk(
    'artwork/getAll',
    async (_, thunkAPI) => {
        try {
            return await artworkService.getArtwork();
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const fetchArtworkCategories = createAsyncThunk(
    'artwork/categories/getAll',
    async (_, thunkAPI) => {
        try {
            return await artworkService.getArtworkCategories();
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const saveArtworkCategory = createAsyncThunk(
    'artwork/categories/save',
    async ({ category, data }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await artworkService.updateArtworkCategory(category, data, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const artworkSlice = createSlice({
    name: 'artwork',
    initialState,
    reducers: {
        reset: (state) => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(createArtwork.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(createArtwork.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                window.location.href = '/admin/artworks'
            })
            .addCase(createArtwork.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getAdminArtworks.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAdminArtworks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.artworks = action.payload;
            })
            .addCase(getAdminArtworks.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getArtworkById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getArtworkById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.artwork = action.payload;
            })
            .addCase(getArtworkById.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(deleteArtwork.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteArtwork.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.artworks.findIndex(artwork => artwork._id === action.payload._id);
                if (index !== -1) {
                    state.artworks[index] = action.payload;
                }
                toast.success('Artwork moved to trash');
            })
            .addCase(deleteArtwork.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(restoreArtwork.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(restoreArtwork.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.artworks.findIndex(artwork => artwork._id === action.payload._id);
                if (index !== -1) {
                    state.artworks[index] = action.payload;
                } else {
                    state.artworks.push(action.payload);
                }
                toast.success('Artwork restored');
            })
            .addCase(restoreArtwork.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(updateArtwork.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(updateArtwork.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.artworks.findIndex(artwork => artwork._id === action.payload._id);
                if (index !== -1) {
                    state.artworks[index] = action.payload;
                }
                toast.success('Artwork updated successfully');
                window.location.href = '/admin/artworks';
            })
            .addCase(updateArtwork.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(updateArtworkFields.fulfilled, (state, action) => {
                const index = state.artworks.findIndex(artwork => artwork._id === action.payload._id);
                if (index !== -1) {
                    state.artworks[index] = action.payload;
                }
            })
            .addCase(updateArtworkFields.rejected, (state, action) => {
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(fetchArtworkCategories.pending, (state) => {
                state.categoriesLoading = true;
                state.categoriesError = false;
                state.categoriesMessage = '';
            })
            .addCase(fetchArtworkCategories.fulfilled, (state, action) => {
                state.categoriesLoading = false;
                state.categories = Array.isArray(action.payload) ? [...action.payload] : [];
                sortCategoriesByPreference(state.categories);
            })
            .addCase(fetchArtworkCategories.rejected, (state, action) => {
                state.categoriesLoading = false;
                state.categoriesError = true;
                state.categoriesMessage = action.payload;
            })
            .addCase(saveArtworkCategory.fulfilled, (state, action) => {
                const updated = action.payload;
                const index = state.categories.findIndex((cat) => cat.category === updated.category);
                if (index !== -1) {
                    state.categories[index] = updated;
                } else {
                    state.categories.push(updated);
                }
                sortCategoriesByPreference(state.categories);
            })
            .addCase(getArtworks.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getArtworks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.artworks = action.payload;
            })
            .addCase(getArtworks.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    },
});

export const { reset } = artworkSlice.actions;
export default artworkSlice.reducer;
